import {Box, Chip, FormControlLabel, Grid, Switch, Typography} from "@mui/material";
import MySelect from "../../../components/MySelect";
import MyTextField from "../../../components/MyTextField";
import BilltoSearch from "../../../components/BilltoSearch";
import RetailLocationSearch from "../../../components/RetailLocationSearch";
import {useSalesRegions} from "../../../hooks/useSalesRegions";
import MultiTerritoryPicker from "../MultiTerritoryPicker";
import FacilityPicker from "../FacilityPicker";

const subLabel = {fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.5};

const FieldView = ({label, value, muted}) => (
    <Box>
        <Typography sx={{fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.25}}>
            {label}
        </Typography>
        <Typography sx={{fontSize: "0.9rem", color: muted ? "text.secondary" : "text.primary", fontStyle: muted ? "italic" : "normal"}}>
            {value}
        </Typography>
    </Box>
);

// Additional Details — conditional fields driven by userTypeConfig, in view or edit mode. Mirrors the
// create wizard's Step 4 field set (order pin, ship-to, bill-to, retail location, admin access, sales
// region, sales territories, facilities), with read-only rendering in view mode.
//
// Props: {editMode, cfg, data, setField, grantsAdmin, businessUnitCode, userId}
const AdditionalDetails = ({editMode, cfg, data, setField, grantsAdmin, businessUnitCode, userId}) => {
    const adminOn = grantsAdmin ? true : !!data.adminPortalAccess;

    const cells = [];
    if (cfg.orderPin) {
        cells.push(
            <Grid item xs={12} sm={4} key="orderPin">
                {editMode ? (
                    <MyTextField value={data.orderPin} editable={true} label="Order Pin" type="text" required={false} onChange={(e) => setField("orderPin", e.target.value)} />
                ) : (
                    <FieldView label="Order Pin" value={data.orderPin || "—"} muted={!data.orderPin} />
                )}
            </Grid>
        );
    }
    if (cfg.shipto) {
        cells.push(
            <Grid item xs={12} sm={4} key="shipto">
                {editMode ? (
                    <MyTextField value={data.shiptoId} editable={true} label="Ship-to" type="text" required={false} onChange={(e) => setField("shiptoId", e.target.value)} />
                ) : (
                    <FieldView label="Ship-to" value={data.shiptoId || "—"} muted={!data.shiptoId} />
                )}
            </Grid>
        );
    }
    if (cfg.billto) {
        cells.push(
            <Grid item xs={12} key="billto">
                {editMode ? (
                    <BilltoSearch
                        label="Bill-to"
                        businessUnitCode={businessUnitCode}
                        editable={true}
                        required={true}
                        initialValue={data.billtoId}
                        onChange={(value) => setField("billtoId", value?.billtoId || "")}
                    />
                ) : (
                    <FieldView label="Bill-to" value={data.billtoId || "—"} muted={!data.billtoId} />
                )}
            </Grid>
        );
    }
    if (cfg.retailLoc) {
        cells.push(
            <Grid item xs={12} key="retailLoc">
                {editMode ? (
                    <RetailLocationSearch
                        label="Retail Location"
                        businessUnitCode={businessUnitCode}
                        billtoId={data.billtoId}
                        editable={true}
                        required={true}
                        initialValue={data.retailLocationId}
                        onChange={(value) => setField("retailLocationId", value?.id || "")}
                    />
                ) : (
                    <FieldView label="Retail Location" value={data.retailLocationId || "—"} muted={!data.retailLocationId} />
                )}
            </Grid>
        );
    }

    return (
        <Box>
            {cells.length > 0 && (
                <Grid container spacing={2}>
                    {cells}
                </Grid>
            )}

            {cfg.hasSalesRegion && (
                <Box sx={{mt: cells.length ? 3 : 0, maxWidth: 360}}>
                    <Typography sx={subLabel}>Sales Region</Typography>
                    {editMode ? (
                        <SalesRegionSelect businessUnitCode={businessUnitCode} userId={userId} value={data.salesRegionId} onChange={(id) => setField("salesRegionId", id)} />
                    ) : (
                        <SalesRegionName businessUnitCode={businessUnitCode} userId={userId} regionId={data.salesRegionId} />
                    )}
                </Box>
            )}

            {cfg.hasSalesTerritories && (
                <MultiTerritoryPicker
                    editMode={editMode}
                    businessUnitCode={businessUnitCode}
                    userId={userId}
                    value={data.salesTerritories ?? []}
                    onChange={(list) => setField("salesTerritories", list)}
                />
            )}

            {cfg.hasFacilities && (
                <FacilityPicker
                    editMode={editMode}
                    businessUnitCode={businessUnitCode}
                    value={data.facilities ?? []}
                    onChange={(list) => setField("facilities", list)}
                />
            )}

            {cfg.adminAccess && (
                <Box sx={{mt: 3}}>
                    <Typography sx={subLabel}>Admin Portal Access</Typography>
                    {editMode ? (
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            <FormControlLabel
                                control={<Switch checked={adminOn} disabled={grantsAdmin} onChange={(e) => setField("adminPortalAccess", e.target.checked)} />}
                                label={adminOn ? "Can access the Admin Portal" : "No Admin Portal access"}
                            />
                            {grantsAdmin && <Chip size="small" label="Granted by group" color="info" variant="outlined" sx={{ml: 1}} />}
                        </Box>
                    ) : (
                        <Typography sx={{fontSize: "0.9rem"}}>{adminOn ? "Enabled" : "Disabled"}</Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

// Single Sales Region selection (region id is the committed value).
const SalesRegionSelect = ({businessUnitCode, userId, value, onChange}) => {
    const regions = useSalesRegions(businessUnitCode, userId, !!businessUnitCode);
    return <MySelect label="Sales Region" options={regions} value={value} required={false} onChange={onChange} />;
};

// Read-only Sales Region display — resolves the region id to its name.
const SalesRegionName = ({businessUnitCode, userId, regionId}) => {
    const regions = useSalesRegions(businessUnitCode, userId, !!businessUnitCode);
    const name = regions.find((r) => r.id === regionId)?.name ?? "";
    return (
        <Typography sx={{fontSize: "0.9rem", color: name ? "text.primary" : "text.secondary", fontStyle: name ? "normal" : "italic"}}>
            {name || "—"}
        </Typography>
    );
};

export default AdditionalDetails;
